import { FC, memo, MouseEvent, useState } from 'react';
import { IconButton, Menu, MenuItem, Typography, Box } from '@material-ui/core';
import { WbUnstyledLink, WbTooltip } from '@agilelab/plugin-wb-platform';
import FormatListBulleted from '@material-ui/icons/FormatListBulleted';
import CenterFocusStrong from '@material-ui/icons/CenterFocusStrong';
import CenterFocusWeakIcon from '@material-ui/icons/CenterFocusWeak';
import CodeIcon from '@material-ui/icons/Code';
import MoreVertIcon from '@material-ui/icons/MoreVert';

interface EntityCardMenuProps {
  short?: boolean;
  focused: boolean;
  onFocusToggle: () => void;
  editorWizardRoute?: string;
  locationTargetUrl?: string;
  errorMessage?: string;
  isLoading?: boolean;
  disableEdit?: boolean;
}

export const EntityCardMenu: FC<EntityCardMenuProps> = memo(
  ({
    short = false,
    focused,
    onFocusToggle,
    editorWizardRoute,
    locationTargetUrl,
    errorMessage = '',
    isLoading = false,
    disableEdit = false,
  }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const editTooltip = disableEdit
      ? 'Editing is disabled when a git tag is selected'
      : errorMessage || 'Edit';
    const focusTooltip = focused ? 'Unfocus' : 'Focus in the descriptor';
    const viewCodeTooltip = 'View Source';

    const openMenu = (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
    };

    const closeMenu = () => {
      setAnchorEl(null);
    };

    const handleFocusClick = (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      onFocusToggle();
      if (short) closeMenu();
    };

    if (short) {
      return (
        <>
          <IconButton
            aria-label="open menu"
            onClick={openMenu}
            size="small"
            style={{ height: 'fit-content' }}
          >
            <MoreVertIcon style={{ fontSize: '16px' }} />
          </IconButton>

          <Menu
            id="entity-menu"
            anchorEl={anchorEl}
            keepMounted
            open={open}
            onClose={closeMenu}
            onClick={e => e.stopPropagation()}
          >
            {editorWizardRoute && (
              <MenuItem
                component={WbUnstyledLink}
                to={editorWizardRoute}
                disabled={Boolean(errorMessage) || disableEdit}
              >
                <FormatListBulleted
                  fontSize="small"
                  style={{ marginRight: 8 }}
                />
                <Typography variant="body2">Edit</Typography>
              </MenuItem>
            )}
            <MenuItem onClick={handleFocusClick}>
              {focused ? (
                <CenterFocusStrong
                  fontSize="small"
                  style={{ marginRight: 8 }}
                />
              ) : (
                <CenterFocusWeakIcon
                  fontSize="small"
                  style={{ marginRight: 8 }}
                />
              )}
              <Typography variant="body2">
                {focused ? 'Unfocus' : 'Focus'}
              </Typography>
            </MenuItem>
            {locationTargetUrl && (
              <MenuItem
                onClick={closeMenu}
                component="a"
                href={locationTargetUrl}
                target="_blank"
                rel="noreferrer"
              >
                <CodeIcon fontSize="small" style={{ marginRight: 8 }} />
                <Typography variant="body2">View Code</Typography>
              </MenuItem>
            )}
          </Menu>
        </>
      );
    }

    return (
      <Box display="flex" alignItems="center">
        {editorWizardRoute && (
          <WbTooltip title={editTooltip} placement="top">
            <span>
              <IconButton
                component={WbUnstyledLink}
                to={editorWizardRoute}
                aria-label="edit"
                size="small"
                disabled={Boolean(errorMessage) || disableEdit}
                style={{ minWidth: 24, minHeight: 24 }}
              >
                <FormatListBulleted style={{ fontSize: '16px' }} />
              </IconButton>
            </span>
          </WbTooltip>
        )}
        <WbTooltip title={focusTooltip} placement="top">
          <IconButton
            disabled={isLoading}
            size="small"
            onClick={handleFocusClick}
            aria-label="toggle focus"
            style={{ minWidth: 24, minHeight: 24 }}
          >
            {focused ? (
              <CenterFocusStrong style={{ fontSize: '18px' }} />
            ) : (
              <CenterFocusWeakIcon style={{ fontSize: '18px' }} />
            )}
          </IconButton>
        </WbTooltip>
        {locationTargetUrl && (
          <WbTooltip title={viewCodeTooltip} placement="top">
            <IconButton
              href={locationTargetUrl}
              target="_blank"
              rel="noreferrer"
              aria-label="view code"
              size="small"
              style={{ minWidth: 24, minHeight: 24 }}
            >
              <CodeIcon style={{ fontSize: '16px' }} />
            </IconButton>
          </WbTooltip>
        )}
      </Box>
    );
  },
);

EntityCardMenu.displayName = 'EntityCardMenu';
